/**
 * Your name and your face — `/me` (§ F120).
 *
 * Jake: *"I should be able to click the player icon then choose to 'change
 * players', or 'change avatar'."* … *"I also want Jake, Mummy, Jack, Elliott to
 * be able to change their names. Internally it is the same player, just the
 * visual changes."* … *"And I want all players to be able to pick from a list of
 * like 40 avatars. Classic, weird, or let them draw one that is stored in our
 * database and used."*
 *
 * One screen for both, because they are the same act — how you appear — and
 * because a rename that needed its own screen would be a screen nobody finds.
 * The forty are `lib/avatars.tsx`; the drawn one is the party surface's own pad
 * (`DrawCanvas`), so the family draws with the tool they already know and the
 * strokes are stored in the format the party badges already render.
 *
 * Saving is per-change, not a form: tapping a face IS choosing it. The name has
 * an explicit Save because a keystroke is not a decision.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, ChevronLeft, KeyRound, LogOut, Trash2, UsersRound } from "lucide-react";
import { AppChrome } from "../components/AppChrome";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { AVATAR_PRESETS } from "../lib/avatars";
import { APP_COLUMN } from "../lib/column";
import { usePlayer } from "../lib/usePlayer";
import { changePassword, deleteAccount, fetchMe, PASSWORD_MIN, updatePhone, type Me } from "../lib/account";
import { forgetAccount, signOutPlayer, type StoredPlayer } from "../lib/player";
import { fetchRoster, saveProfile, type AvatarValue } from "../lib/roster";
import { setAvatarDraft } from "../lib/avatar-draft";
import { glyphToDrawing } from "../lib/glyph-to-drawing";
import { STROKE_WIDTHS, type Drawing } from "../party/drawing";
import { DrawCanvas } from "../party/components";
import { PLAYER_PALETTE, sessionColorFor } from "../party/palette";

/**
 * § F123 — what a drawn avatar may weigh. Jake: *"Stick with the 10KB limit."*
 * That is the party avatar pad's own cap (`mini` mode), which is what these
 * drawings have always been sized against; the server's 64KB is the outer
 * bound, not the target.
 */
const AVATAR_DRAW_BYTES = 10 * 1024;

/**
 * § F124 — the pad's two pens, moved up a step twice on Jake's word.
 *
 * First: *"Apply the width of the big pen to the small pin (small circle), and
 * make the big pin x2 as wide as it is now"* — `[5, 14]` became `[14, 28]`.
 * Then, on seeing it: *"The current FAT pen is what I want for the small pen,
 * and I want the fat pen to be 2x fatter"* — so `[28, 56]`. An avatar is read
 * at 36px in a header, where the full canvas's pens are hairlines.
 */
const AVATAR_PEN_WIDTHS = [STROKE_WIDTHS[1] * 2, STROKE_WIDTHS[1] * 4] as const;

/**
 * § F126 — ONE PALETTE FOR BOTH. Jake: *"Background needs both black and white.
 * The same color pallet for Primary and Background."* The app's audited ten,
 * plus black and white — whichever of the two you are choosing.
 */
const COLOR_CHOICES = ["#000000", "#FFFFFF", ...PLAYER_PALETTE.map((slot) => slot.hex)];
const INK_CHOICES = COLOR_CHOICES;

function safeReturn(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

export default function MePage() {
  const { player, setPlayer } = usePlayer();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const back = safeReturn(search.get("from"));

  const [avatar, setAvatar] = useState<AvatarValue | null>(null);
  /** § F122 — the two chosen colours; null means "the derived tone". */
  const [color, setColor] = useState<string | null>(null);
  const [ink, setInk] = useState<string | null>(null);
  const [name, setName] = useState(player?.name ?? "");
  /** The pad's seed (a preset converted to strokes), and its remount key. */
  const [seed, setSeed] = useState<Drawing | null>(null);
  const [seedKey, setSeedKey] = useState(0);
  /**
   * § F124 — which preset the pad was started from. Jake: *"Clicking Start
   * Again should reapply whatever avatar and colors I have selected above."* So
   * "start again" is not "empty" — it is "back to the picture I chose", in
   * whatever colours are selected NOW.
   */
  const [seedPreset, setSeedPreset] = useState<(typeof AVATAR_PRESETS)[number] | null>(null);
  /** Whatever is on the pad right now — a colour change repaints THIS. */
  const currentRef = useRef<Drawing | null>(null);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef<number | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  /**
   * The live tone: the CHOSEN colours where they exist, the derived session
   * tone where they do not. Declared up here because the seeding callback
   * needs the ink to draw a preset with.
   */
  const derived = sessionColorFor(player?.id ?? "");
  const tone = { hex: color ?? derived.hex, ink: ink ?? derived.ink };
  /** The latest `commit`, so the debounced save never closes over a stale one. */
  const commitRef = useRef<(changes: Parameters<typeof saveProfile>[1]) => Promise<void>>(
    async () => {}
  );
  const [error, setError] = useState("");

  // § F123 — the preview belongs to THIS screen: it goes when the screen does.
  useEffect(() => () => setAvatarDraft(null), []);

  // Nobody is playing: this screen has no subject. Go and pick one.
  useEffect(() => {
    if (!player) navigate(`/who?from=${encodeURIComponent(back)}`, { replace: true });
  }, [back, navigate, player]);

  // The saved avatar comes from the roster, which is also what the picker draws.
  useEffect(() => {
    if (!player) return;
    let live = true;
    fetchRoster()
      .then((rows) => {
        if (!live) return;
        const me = rows.find((row) => row.id === player.id);
        if (me) {
          setAvatar(me.avatar);
          setName(me.display_name);
          setColor(me.color);
          setInk(me.ink);
        }
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [player?.id]);

  /**
   * § F123 — SAVE AS YOU DRAW. Debounced, because a stroke is not a decision
   * and a PATCH per point would be: 800ms after the pen stops, the drawing is
   * stored. The header preview does not wait for it (`setAvatarDraft`), so the
   * screen feels instant either way.
   */
  const scheduleSave = useCallback(
    (next: Drawing) => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      setSaving(true);
      saveTimer.current = window.setTimeout(() => {
        saveTimer.current = null;
        void commitRef.current({ avatar: { kind: "drawn", drawing: next } }).finally(() =>
          setSaving(false)
        );
      }, 800);
    },
    []
  );

  const onDraw = useCallback(
    (next: Drawing) => {
      currentRef.current = next;
      setAvatarDraft({ kind: "drawn", drawing: next });
      scheduleSave(next);
    },
    [scheduleSave]
  );

  /**
   * § F126 — ONE COLOUR FOR THE DRAWING. Jake: *"I get one color for the
   * drawing and one color for the background. Any time I pick a new color it
   * updates the scratchpad."* So the primary is not a pen the player carries
   * between strokes — it is the colour of the WHOLE picture, and changing it
   * repaints what is already there rather than applying to whatever comes next.
   */
  const recolour = useCallback(
    (hex: string) => {
      const drawing = currentRef.current;
      if (!drawing || drawing.strokes.length === 0) return;
      const next: Drawing = {
        ...drawing,
        strokes: drawing.strokes.map((stroke) => ({ ...stroke, c: hex })),
      };
      currentRef.current = next;
      setSeed(next);
      setSeedKey((n) => n + 1);
      setAvatarDraft({ kind: "drawn", drawing: next });
      scheduleSave(next);
    },
    [scheduleSave]
  );

  /** A preset is a STARTING POINT: convert it to strokes and drop it in. */
  const seedFrom = useCallback(
    (preset: (typeof AVATAR_PRESETS)[number]) => {
      const drawing = glyphToDrawing(preset.glyph, tone.ink);
      if (!drawing) return;
      setSeedPreset(preset);
      currentRef.current = drawing;
      setSeed(drawing);
      setSeedKey((n) => n + 1);
      setAvatarDraft({ kind: "drawn", drawing });
      scheduleSave(drawing);
    },
    [scheduleSave, tone.ink]
  );

  const commit = useCallback(
    async (changes: {
      display_name?: string | null;
      avatar?: AvatarValue | null;
      color?: string | null;
      ink?: string | null;
    }) => {
      if (!player) return;
      setError("");
      try {
        const display = await saveProfile(player, changes);
        if (changes.avatar !== undefined) {
          setAvatar(changes.avatar ?? null);
          // Saved: the roster is the truth again, so drop the preview.
          setAvatarDraft(null);
        }
        if (changes.color !== undefined) setColor(changes.color ?? null);
        if (changes.ink !== undefined) setInk(changes.ink ?? null);
        /*
         * § F122 (2026-08-21) — DO NOT TOUCH THE NAME FIELD UNLESS THE NAME WAS
         * WHAT CHANGED. Jake: *"picking a player avatar resets the name I typed
         * (before hitting save)."* Every save returns the stored display name,
         * and this used to write it back into the input — so tapping a face
         * mid-edit threw the typing away. The identity still updates, because
         * that is what the rest of the app reads.
         */
        if (display) {
          if (changes.display_name !== undefined) setName(display);
          setPlayer({ ...player, name: display });
        }
        setSaved("Saved");
        window.setTimeout(() => setSaved(null), 1500);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't save that");
      }
    },
    [player, setPlayer]
  );

  // The debounced save reads this, so it always calls the current closure.
  commitRef.current = commit;

  if (!player) return null;

  /*
   * § F127 — the colours live INSIDE the pad now, above its tools: they are
   * what it paints with, and Jake asked for them there ("Move the color pickers
   * to inside the scratchpad, above the pen icons, erase, and undo").
   */
  const colourPickers = (
    <div className="w-full flex flex-col gap-2">
                {/* § F123 — Jake: *"Change section 'Primary' to 'Background' and move
                    it below what is currently 'Secondary'. Rename 'Secondary' to
                    'primary'."* The glyph is the thing you pick first, so it is
                    PRIMARY and it comes first; the tile behind it is the
                    BACKGROUND. The stored columns keep their names — `avatar_ink`
                    is the mark, `avatar_color` is the field behind it. */}
                {(
                  [
                    ["ink", "Primary", INK_CHOICES, ink],
                    ["color", "Background", COLOR_CHOICES, color],
                  ] as const
                ).map(([field, label, choices, chosen]) => (
                  <div key={field} className="flex flex-col gap-2">
                    <p className="font-heading font-extrabold uppercase tracking-wide text-xs">
                      {label}
                    </p>
                    <div className="grid grid-cols-6 [@media(min-width:600px)]:grid-cols-12 gap-2">
                      {choices.map((hex) => (
                        <button
                          key={hex}
                          type="button"
                          aria-label={`${label} ${hex}`}
                          data-testid={`me-${field}-${hex.slice(1).toLowerCase()}`}
                          data-chosen={chosen?.toUpperCase() === hex.toUpperCase() ? "1" : "0"}
                          onClick={() => {
                            // § F126 — the primary is the drawing's colour, so
                            // choosing it repaints what is on the pad.
                            if (field === "ink") recolour(hex);
                            void commit({ [field]: hex });
                          }}
                          className={`aspect-square rounded-block ink-border btn-press ${
                            chosen?.toUpperCase() === hex.toUpperCase() ? "ring-4 ring-black" : ""
                          }`}
                          style={{ background: hex }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
    </div>
  );

  return (
    <AppChrome lock={false} column="menu">
      <main
        className="flex-1 flex items-start justify-center p-3 [@media(min-height:700px)]:p-6"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
      >
        <div className={`${APP_COLUMN} flex flex-col gap-3`} data-testid="me-page">
          {/* ── who you are (§ F127) ──────────────────────────────────────────
              Jake: *"remove the section 'YOUR NAME'. Change the name in the
              section above to an editable field with a save icon. This saves a
              lot of space. I also don't need any of this: Your scores and
              history stay yours…"* So the header card IS the name field: the
              face, the input and one ✓. The rename rule it used to spell out is
              § F120's and has not changed. */}
          <div className="bg-white ink-border rounded-block-lg block-shadow-2 p-3 flex items-center gap-2">
            <Link
              to={back}
              aria-label="Back"
              data-testid="me-back"
              className="shrink-0 w-9 h-9 inline-flex items-center justify-center text-black/70 btn-press"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={3} />
            </Link>
            <PlayerAvatar playerId={player.id} avatar={avatar} color={color} ink={ink} size={44} />
            <input
              aria-label="Your name"
              data-testid="me-name-input"
              value={name}
              maxLength={40}
              onChange={(e) => setName(e.target.value.slice(0, 40))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && name.trim()) void commit({ display_name: name.trim() });
              }}
              className="min-w-0 flex-1 bg-white ink-border rounded-block px-3 py-2 outline-none font-heading font-extrabold uppercase tracking-tight text-lg"
            />
            <button
              type="button"
              aria-label="Save your name"
              data-testid="me-name-save"
              disabled={name.trim().length === 0}
              onClick={() => void commit({ display_name: name.trim() })}
              className="shrink-0 w-11 h-11 bg-black text-white ink-border rounded-block block-shadow-1 btn-press btn-off flex items-center justify-center"
            >
              <Check className="w-5 h-5" strokeWidth={3} />
            </button>
          </div>

          {/* ── the forty (§ F123: one list; § F126: at the top) ─────────────── */}
          <div className="bg-white ink-border rounded-block-lg block-shadow-1 p-3 flex flex-col gap-2">
            <p className="font-heading font-extrabold uppercase tracking-wide text-xs">Avatar</p>
            <div className="grid grid-cols-5 [@media(min-width:600px)]:grid-cols-8 gap-2">
              {AVATAR_PRESETS.map((preset) => {
                /* § F124 — the ring marks the glyph the pad was STARTED from:
                   a saved avatar is a drawing (§ F123), so there is no preset
                   id on it to compare against. */
                const chosen = seedPreset?.id === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    title={preset.label}
                    aria-label={preset.label}
                    data-testid={`me-avatar-${preset.id}`}
                    data-chosen={chosen ? "1" : "0"}
                    onClick={() => seedFrom(preset)}
                    className={`aspect-square rounded-block ink-border btn-press flex items-center justify-center ${
                      chosen ? "ring-4 ring-black" : ""
                    }`}
                    style={{ background: tone.hex, color: tone.ink }}
                  >
                    <preset.glyph className="w-5 h-5" strokeWidth={2.5} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── the scratchpad (§ F123–§ F127) ───────────────────────────────
              No heading; the colours live inside it, above its tools; it saves
              itself as you draw. */}
          <div className="bg-white ink-border rounded-block-lg block-shadow-1 p-3 flex flex-col gap-2">
            <div className="flex items-center justify-end">
              <span data-testid="me-autosave" className="font-body text-[11px] text-black/70">
                {saving ? "Saving…" : "Saves as you draw"}
              </span>
            </div>

            <div className="flex flex-col items-center gap-2" data-testid="me-draw-pad">
              <DrawCanvas
                key={seedKey}
                initial={seed}
                paper={tone.hex}
                penColor={tone.ink}
                widths={AVATAR_PEN_WIDTHS}
                showEraser
                showPens={false}
                toolLabels={false}
                showSubmit={false}
                showStrokeCount={false}
                maxBytes={AVATAR_DRAW_BYTES}
                belowCanvas={colourPickers}
                onChange={onDraw}
              />
            </div>
          </div>

          {/* § F127 — *"Add a 'Save' bar to the very bottom under the
              scratchpad."* Everything here already saves itself; this is the
              DONE button — it flushes what is still in flight (a name typed and
              not confirmed, a stroke inside the debounce) and takes the player
              back where they came from. */}
          <button
            type="button"
            data-testid="me-save-bar"
            onClick={() => {
              const trimmed = name.trim();
              const pending = currentRef.current;
              void (async () => {
                if (trimmed) await commit({ display_name: trimmed });
                if (pending) await commit({ avatar: { kind: "drawn", drawing: pending } });
                navigate(back);
              })();
            }}
            className="w-full min-h-[52px] bg-black text-white ink-border rounded-block font-heading font-extrabold uppercase tracking-wide text-sm block-shadow-1 btn-press flex items-center justify-center gap-2"
          >
            <Check className="w-5 h-5" strokeWidth={3} />
            Save
          </button>

          <AccountCard player={player} />

          {saved && (
            <p
              data-testid="me-saved"
              className="text-center font-heading font-extrabold uppercase tracking-wide text-xs"
            >
              {saved}
            </p>
          )}
          {error && (
            <p
              data-testid="me-error"
              className="text-brand-secondary text-center font-bold uppercase tracking-wide text-xs"
            >
              {error}
            </p>
          )}
        </div>
      </main>
    </AppChrome>
  );
}

/**
 * § F201 — the account: the email you sign in with (not editable — it IS the
 * sign-in), an optional phone, your groups, sign out, and delete. Jake: *"Name,
 * email is required, phone # is optional, but required if the user wants to get
 * reminders/nudges (not implemented yet)."*
 */
function AccountCard({ player }: { player: StoredPlayer }) {
  const [me, setMe] = useState<Me | null>(null);
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchMe(player)
      .then((m) => {
        setMe(m);
        setPhone(m.phone ?? "");
      })
      .catch(() => {});
  }, [player.id]);

  const savePhone = async () => {
    setNote("");
    try {
      const { phone: stored } = await updatePhone(phone.trim());
      setPhone(stored ?? "");
      setNote("Saved");
    } catch (err) {
      setNote((err as Error).message);
    }
  };

  const remove = async () => {
    try {
      await deleteAccount();
      forgetAccount(player.id);
      navigate("/", { replace: true });
    } catch (err) {
      setNote((err as Error).message);
    }
  };

  const label = "font-heading font-extrabold uppercase tracking-wide text-xs";
  const btn =
    "min-h-[44px] px-3 ink-border rounded-block font-heading font-extrabold uppercase tracking-wide text-xs btn-press btn-off inline-flex items-center justify-center gap-1.5";

  return (
    <div data-testid="me-account" className="bg-white ink-border rounded-block-lg block-shadow-1 p-3 flex flex-col gap-3">
      <p className={label}>Account</p>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-black/70">Email (you sign in with this)</span>
        <span data-testid="me-email" className="font-bold break-all">
          {me?.email ?? "…"}
        </span>
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-black/70">Phone (optional — only for reminders you turn on)</span>
        <div className="flex gap-2">
          <input
            data-testid="me-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value.slice(0, 24))}
            autoComplete="tel"
            className="min-w-0 flex-1 bg-white ink-border rounded-block px-3 py-2 outline-none font-bold text-base"
          />
          <button type="button" data-testid="me-phone-save" onClick={() => void savePhone()} className={`${btn} bg-black text-white`}>
            <Check className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>
      </label>
      {note && (
        <p data-testid="me-account-note" className="text-xs font-bold">
          {note}
        </p>
      )}
      <ChangePassword />
      <div className="flex flex-wrap gap-2">
        <Link to="/groups" data-testid="me-groups" className={`${btn} bg-brand-primary`}>
          <UsersRound className="w-4 h-4" strokeWidth={2.5} /> My groups
        </Link>
        <button
          type="button"
          data-testid="me-sign-out"
          onClick={() => void signOutPlayer(player).then(() => navigate("/", { replace: true }))}
          className={`${btn} bg-white`}
        >
          <LogOut className="w-4 h-4" strokeWidth={2.5} /> Sign out
        </button>
      </div>
      {confirming ? (
        <div className="flex flex-col gap-2 bg-brand-secondary/10 ink-border rounded-block p-2">
          <p className="text-sm">
            This deletes your account, your scores, your duels and your drawings, and takes you out of every group. It
            can't be undone. Type <strong>DELETE</strong> to confirm.
          </p>
          <div className="flex gap-2">
            <input
              data-testid="me-delete-typed"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="min-w-0 flex-1 bg-white ink-border rounded-block px-3 py-2 outline-none font-bold text-base"
            />
            <button
              type="button"
              data-testid="me-delete-confirm"
              disabled={typed !== "DELETE"}
              onClick={() => void remove()}
              className={`${btn} bg-black text-white`}
            >
              Delete
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="me-delete"
          onClick={() => setConfirming(true)}
          className="self-start underline text-xs font-bold inline-flex items-center gap-1 min-h-[32px]"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete my account
        </button>
      )}
    </div>
  );
}

/**
 * § F206 — change password. Jake: *"I need a proper 'forgot password' and
 * 'change password'. Both of these come with standard emails to process."*
 * Needs the current one; this device stays signed in, every other one is signed
 * out, and the owner gets a "your password was changed" email.
 */
function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const ready = current.length > 0 && next.length >= PASSWORD_MIN && confirm === next;
  const field = "w-full bg-white ink-border rounded-block px-3 py-2 outline-none font-bold text-base";
  const btn =
    "min-h-[44px] px-3 ink-border rounded-block font-heading font-extrabold uppercase tracking-wide text-xs btn-press btn-off inline-flex items-center justify-center gap-1.5";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !ready) return;
    setBusy(true);
    setNote("");
    try {
      await changePassword(current, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      setOpen(false);
      setNote("Password changed. We emailed you to confirm, and signed out your other devices.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {open ? (
        <form onSubmit={(e) => void submit(e)} data-testid="me-password-form" className="flex flex-col gap-2 bg-brand-primary/20 ink-border rounded-block p-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-black/70">Current password</span>
            <input data-testid="me-password-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className={field} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-black/70">New password (at least {PASSWORD_MIN} characters)</span>
            <input data-testid="me-password-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className={field} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-black/70">Confirm new password</span>
            <input data-testid="me-password-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field} />
            {confirm.length > 0 && confirm !== next && <span className="text-xs font-bold">The passwords don't match.</span>}
          </label>
          <div className="flex gap-2">
            <button type="submit" data-testid="me-password-save" disabled={busy || !ready} className={`${btn} flex-1 bg-black text-white`}>
              {busy ? "Saving…" : "Change password"}
            </button>
            <button type="button" onClick={() => setOpen(false)} className={`${btn} flex-1 bg-white`}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button type="button" data-testid="me-password" onClick={() => { setOpen(true); setNote(""); }} className={`${btn} self-start bg-white`}>
          <KeyRound className="w-4 h-4" strokeWidth={2.5} /> Change password
        </button>
      )}
      {note && (
        <p data-testid="me-password-note" className="text-xs font-bold">
          {note}
        </p>
      )}
    </div>
  );
}
